import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({ errorInfo });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div
                    style={{
                        padding: '2rem',
                        maxWidth: '600px',
                        margin: '4rem auto',
                        textAlign: 'center',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                    }}
                >
                    <h1 style={{ color: '#ef4444', marginBottom: '1rem' }}>
                        Something went wrong
                    </h1>
                    <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
                        We encountered an unexpected error. Please try refreshing the page.
                    </p>
                    {this.state.error && (
                        <details
                            style={{
                                marginBottom: '2rem',
                                padding: '1rem',
                                background: '#f9fafb',
                                borderRadius: '0.5rem',
                                textAlign: 'left',
                            }}
                        >
                            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                                Error details
                            </summary>
                            <pre
                                style={{
                                    marginTop: '1rem',
                                    fontSize: '0.875rem',
                                    overflow: 'auto',
                                    color: '#ef4444',
                                }}
                            >
                                {this.state.error.toString()}
                            </pre>
                        </details>
                    )}
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                        <button
                            onClick={this.handleReset}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: '#6366f1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.5rem',
                                cursor: 'pointer',
                                fontWeight: 600,
                            }}
                        >
                            Try Again
                        </button>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: '#e5e7eb',
                                color: '#374151',
                                border: 'none',
                                borderRadius: '0.5rem',
                                cursor: 'pointer',
                                fontWeight: 600,
                            }}
                        >
                            Refresh Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
